import { AccordionItem } from '@ingka/accordion';
import Checkbox from '@ingka/checkbox';
import RadioButton from '@ingka/radio-button';
import React, { useRef } from 'react';
import { ANALYTICS_OM_FILTERS_MAP } from 'types/analytics';
import { CheckBox } from 'types/appContext';
import { t } from 'i18next';
import { OMFilter as OMFilterCategory } from 'types/enums';
import { ORG_SELECTION_SELECT_ALL } from 'hooks/useOrgSelection/useOrgSelection.utils';
import classes from './OMFilters.module.scss';
import { CheckBoxLabel } from './CheckBoxLabel';

const getIndeterminateState = (checkboxes: CheckBox[] | undefined): boolean => (
    !!checkboxes && checkboxes.some(el => el.defaultChecked) && checkboxes.some(el => !el.defaultChecked)
);

/**
 * @param caption
 * @returns Adds custom caption, if omitted checked checkbox label property is used
 */
const OMFilter = (
    {
        data,
        handleChange,
        category,
        title,
        selectionType,
        autoCollapse,
        caption,
        showSelectAll,
        sortingToggle,
    }
    : {
        data: CheckBox[] | undefined,
        handleChange: (category: OMFilterCategory, key: string, type?: string) => void,
        category: OMFilterCategory,
        title: string,
        selectionType: 'single' | 'multi',
        autoCollapse?: boolean,
        caption?: string,
        showSelectAll?: boolean,
        sortingToggle?: JSX.Element
    }
) => {
    const ref = useRef<HTMLLIElement>(null);
    const createProps = (item: CheckBox) => ({
        autoComplete: Boolean(item.autocomplete).toString(),
        id: item.category === OMFilterCategory.COST_CENTER ? `${item.id}-${item.label}` : `${item.id}-${item.value}`,
        name: item.category,
        value: item.value,
        checked: item.defaultChecked,
        disabled: item.disabled,
        onChange: () => {
            handleChange(category, item.value, category === OMFilterCategory.UNIT ? item.type : undefined);
            if (autoCollapse) {
                setTimeout(() => ref.current?.getElementsByTagName('button')[0].click(), 0);
            }
        },
        label: (<CheckBoxLabel label={item.label} subLabel={item.subLabel ?? ''} />)
    });

    const renderSelectionItems = (checkboxItem: CheckBox) => {
        if (selectionType === 'single') {
            return (
                <RadioButton
                    {...createProps(checkboxItem)}
                />
            );
        }

        return (
            <Checkbox
                {...createProps(checkboxItem)}
            />
        );
    };

    return (
        <AccordionItem
            data-testid={`accordion-item-${category}`}
            title={t(title)}
            id={`acc-item-${category}`}
            key={`acc-item-${category}`}
            disabled={!data || data.length === 0}
            caption={caption}
            ref={ref}
            data-analytics={ANALYTICS_OM_FILTERS_MAP.get(category)}
        >
            {sortingToggle}
            {showSelectAll && (
                <div className={`${classes['checkbox-container']} ${classes['checkbox-container--select-all']}`}>
                    <Checkbox
                        id={`select-all-${category}`}
                        label={t('SELECT_ALL')}
                        name={`select-all-${category}`}
                        checked={data?.every((item: CheckBox) => item.defaultChecked)}
                        onChange={() => handleChange(category, ORG_SELECTION_SELECT_ALL)}
                        value={t('SELECT_ALL')}
                        indeterminate={getIndeterminateState(data)}
                    />
                </div>
            )}
            {data?.map((item: CheckBox) => (
                // FIXME: Temporary fix for duplicate id's in departments
                <div
                    key={category === OMFilterCategory.DEPARTMENT ? window.crypto.randomUUID() : `${item.id}-${item.label}`}
                    className={`${classes['checkbox-container']} ${showSelectAll ? classes['checkbox-container--indented'] : ''}`}
                >
                    {renderSelectionItems(item)}
                </div>
            ))}
        </AccordionItem>
    );
};

export default OMFilter;
